import React from 'react';
import './Menu.css';

/**
 * The one menu implementation in the app. Everything that shows a list of
 * commands renders these items: the toolbar's menu bar, the network canvas's
 * right-click menu, and the elements tree's per-row menu. They differ only in
 * how the panel is anchored — under a bar button, or at the pointer — so that's
 * the only thing {@link MenuBar} and {@link ContextMenu} add on top of
 * {@link MenuList}.
 */

/**
 * A single entry in a menu. An `action` runs a callback; a `submenu` opens a
 * nested flyout of more items; a `separator` draws a divider line.
 */
export type MenuItem =
  | {
      kind: 'action';
      label: string;
      icon?: string;
      shortcut?: string;
      disabled?: boolean;
      checked?: boolean;
      /** Destructive (delete, remove) — rendered in red. */
      danger?: boolean;
      onClick: () => void;
    }
  | { kind: 'submenu'; label: string; icon?: string; items: MenuItem[] }
  | { kind: 'separator' };

/** A top-level menu in the menu bar (e.g. "File", "Edit"). */
export interface TopMenu {
  label: string;
  items: MenuItem[];
}

/**
 * Renders a list of menu items as a dropdown panel. Submenus expand to the
 * right on hover. `onClose` is called after any action item is chosen so the
 * whole menu tree collapses.
 */
export function MenuList({
  items,
  onClose,
  touch,
}: {
  items: MenuItem[];
  onClose: () => void;
  /** Size the rows for fingers rather than a cursor (the mobile layout). */
  touch?: boolean;
}) {
  const [openSubmenu, setOpenSubmenu] = React.useState<number | null>(null);

  return (
    <div className={`menu-panel${touch ? ' menu-panel-touch' : ''}`}>
      {items.map((item, index) => {
        if (item.kind === 'separator') {
          return <div key={index} className="menu-item-separator" />;
        }

        if (item.kind === 'submenu') {
          return (
            <div
              key={index}
              className="menu-item menu-item-submenu"
              onMouseEnter={() => setOpenSubmenu(index)}
              onMouseLeave={() => setOpenSubmenu(null)}
            >
              <span className="menu-item-main">
                {item.icon && <span className="menu-item-icon">{item.icon}</span>}
                <span className="menu-item-label">{item.label}</span>
              </span>
              <span className="menu-item-arrow">▸</span>
              {openSubmenu === index && (
                <div className="menu-submenu-panel">
                  <MenuList items={item.items} onClose={onClose} touch={touch} />
                </div>
              )}
            </div>
          );
        }

        return (
          <button
            key={index}
            type="button"
            className={`menu-item menu-item-action${item.danger ? ' menu-item-danger' : ''}`}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              item.onClick();
              onClose();
            }}
          >
            <span className="menu-item-main">
              <span className="menu-item-icon">{item.checked ? '✓' : item.icon ?? ''}</span>
              <span className="menu-item-label">{item.label}</span>
            </span>
            {item.shortcut && <span className="menu-item-shortcut">{item.shortcut}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A classic menu bar: a row of top-level menus, each opening a dropdown on
 * click. Once one menu is open, hovering another switches to it. Clicking
 * outside closes any open menu.
 */
export function MenuBar({ menus }: { menus: TopMenu[] }) {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const barRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (openIndex === null) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) {
        setOpenIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openIndex]);

  return (
    <div className="menu-bar" ref={barRef}>
      {menus.map((menu, index) => (
        <div key={menu.label} className="menu-bar-item">
          <button
            type="button"
            className={`menu-bar-button${openIndex === index ? ' menu-bar-button-open' : ''}`}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            onMouseEnter={() => {
              if (openIndex !== null) setOpenIndex(index);
            }}
          >
            {menu.label}
          </button>
          {openIndex === index && (
            <div className="menu-dropdown">
              <MenuList items={menu.items} onClose={() => setOpenIndex(null)} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Where a context menu wants to appear — viewport coordinates. */
export interface MenuAnchor {
  x: number;
  y: number;
}

interface ContextMenuProps {
  /** Viewport coordinates of the click that opened the menu. */
  anchor: MenuAnchor;
  items: MenuItem[];
  /** Size the rows for fingers rather than a cursor (the mobile layout). */
  touch?: boolean;
  onClose: () => void;
}

const EDGE_GAP = 4;

/**
 * The same menu panel, anchored at a point instead of under a bar button:
 * right-clicking a graph node, or a row of the elements tree.
 *
 * Rendered at the anchor and then nudged back inside the viewport, since the
 * things that open one — the network canvas, the tree in the bottom-left
 * quadrant — sit near an edge often enough that a menu hanging off the screen
 * is the common case, not the exotic one. A full-screen backdrop closes it on
 * any click, and so does Escape.
 */
export function ContextMenu({ anchor, items, touch, onClose }: ContextMenuProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState(anchor);

  // Measured before paint, so the menu never appears at the raw anchor first.
  React.useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const { width, height } = panel.getBoundingClientRect();
    setPosition({
      x: Math.max(EDGE_GAP, Math.min(anchor.x, window.innerWidth - width - EDGE_GAP)),
      y: Math.max(EDGE_GAP, Math.min(anchor.y, window.innerHeight - height - EDGE_GAP)),
    });
  }, [anchor.x, anchor.y, items]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <>
      <div
        className="menu-context-backdrop"
        onClick={onClose}
        onContextMenu={(event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <div className="menu-context" ref={panelRef} style={{ left: position.x, top: position.y }}>
        <MenuList items={items} onClose={onClose} touch={touch} />
      </div>
    </>
  );
}
